import { useState } from 'react';
import { Anchor, Button, Group, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import type { KimlikTipi } from '@isg/shared-types';
import { useContractors, useIstihdamAc, useKisiAc, useWorkplacesTumu } from '../api/queries';
import { KIMLIK_TIPI_ETIKET } from '../i18n/tr';
import { YazmaHatasi } from './organizasyon-parts';

const ANA_FIRMA = 'ana-firma';

const KIMLIK_TIPLERI: readonly KimlikTipi[] = ['tc', 'yabanci_kimlik', 'pasaport'];

/**
 * KİŞİ EKLEME (TUR 9 / B1 — P1: kişi BAĞIMSIZ bir kayıttır, kendi sayfası
 * vardır). `POST /api/persons` F1·S2'den beri duruyordu ve **hiçbir ekran
 * onu çağırmıyordu**: yeni bir çalışan ancak toplu aktarma dosyasıyla ya da
 * seed'le sisteme girebiliyordu.
 *
 * ⚠️ **İLK İSTİHDAM AYNI SAYFADA, AMA AYRI BİR KAYITTIR** (§2.7 — kişi ≠
 * istihdam): kişi kaydı açılır, sonra istihdam açılır. Kişi açılıp istihdam
 * düşerse **kişi kalır** ve kullanıcıya söylenir — sessizce geri sarılmaz;
 * kişi dosyası zaten tenant düzeyindedir ve istihdamsız da yaşar.
 *
 * ⚠️ **KİMLİK NUMARASI SONRADAN DEĞİŞTİRİLEMEZ** (K11): üç giriş kanalının
 * eşleştirme çapasıdır. Bu yüzden formda uyarısı durur.
 * ⚠️ **AYNI KİMLİKLE SİLİNMİŞ DOSYA VARSA sunucu 409 + `restore` önerir** —
 * mesaj olduğu gibi gösterilir (D2).
 */
export function KisiEkleScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [kimlikTipi, setKimlikTipi] = useState<KimlikTipi>('tc');
  const [kimlikNo, setKimlikNo] = useState('');
  const [adSoyad, setAdSoyad] = useState('');
  const [dogumTarihi, setDogumTarihi] = useState('');
  const [email, setEmail] = useState('');
  const [sicil, setSicil] = useState('');
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [isveren, setIsveren] = useState<string>(ANA_FIRMA);
  const [rol, setRol] = useState('');
  const [baslangic, setBaslangic] = useState('');
  const [istihdamUyarisi, setIstihdamUyarisi] = useState<string | null>(null);

  const isyerleri = useWorkplacesTumu({ durum: 'aktif' });
  const contractors = useContractors();
  const kisiAc = useKisiAc();
  const istihdamAc = useIstihdamAc();

  const kisiDolu = kimlikNo.trim() !== '' && adSoyad.trim() !== '' && dogumTarihi !== '';
  const istihdamDolu = workplaceId !== null && rol.trim() !== '' && baslangic !== '';

  const kaydet = (): void => {
    setIstihdamUyarisi(null);
    kisiAc.mutate(
      {
        kimlikTipi,
        kimlikNo: kimlikNo.trim(),
        adSoyad: adSoyad.trim(),
        dogumTarihi,
        email: email.trim() === '' ? null : email.trim(),
        personelSicilNo: sicil.trim() === '' ? null : sicil.trim(),
      },
      {
        onSuccess: (kisi) => {
          if (!istihdamDolu || !workplaceId) {
            void navigate(`/calisanlar/${kisi.id}`);
            return;
          }
          istihdamAc.mutate(
            {
              personId: kisi.id,
              workplaceId,
              contractorId: isveren === ANA_FIRMA ? null : isveren,
              rol: rol.trim(),
              baslangic,
            },
            {
              onSuccess: () => void navigate(`/calisanlar/${kisi.id}`),
              // ⚠️ KİŞİ AÇILDI, İSTİHDAM DÜŞTÜ: geri sarmıyoruz ve bunu
              // SÖYLÜYORUZ. Sessizce yutmak, kullanıcıya kaydın hiç
              // açılmadığını düşündürürdü (§2.40).
              onError: (hata) => {
                setIstihdamUyarisi(
                  `Kişi kaydedildi ama istihdam açılamadı: ${hata.message}. ` +
                    'Kişi kartından istihdam ekleyebilirsiniz.',
                );
                void navigate(`/calisanlar/${kisi.id}`);
              },
            },
          );
        },
      },
    );
  };

  return (
    <Stack data-testid="kisi-ekle-sayfasi">
      <Anchor component={Link} to="/calisanlar" size="sm">
        ← Çalışanlar
      </Anchor>
      <Title order={3}>Kişi ekle</Title>
      <Group align="flex-end" gap="sm">
        <Select
          label="Kimlik tipi"
          data-testid="kisi-ekle-kimlik-tipi"
          allowDeselect={false}
          value={kimlikTipi}
          onChange={(v) => setKimlikTipi((v as KimlikTipi | null) ?? 'tc')}
          data={KIMLIK_TIPLERI.map((t) => ({
            value: t,
            label: KIMLIK_TIPI_ETIKET[t] ?? t,
          }))}
          w={180}
        />
        <TextInput
          label="Kimlik no"
          value={kimlikNo}
          onChange={(e) => setKimlikNo(e.currentTarget.value)}
          data-testid="kisi-ekle-kimlik-no"
          w={200}
        />
        <TextInput
          label="Ad soyad"
          value={adSoyad}
          onChange={(e) => setAdSoyad(e.currentTarget.value)}
          data-testid="kisi-ekle-ad"
          w={260}
        />
        <TextInput
          label="Doğum tarihi"
          type="date"
          value={dogumTarihi}
          onChange={(e) => setDogumTarihi(e.currentTarget.value)}
          data-testid="kisi-ekle-dogum"
        />
      </Group>
      <Group align="flex-end" gap="sm">
        <TextInput
          label="E-posta"
          description="Hesap eşleşmesinin ekseni"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          data-testid="kisi-ekle-eposta"
          w={280}
        />
        <TextInput
          label="Personel sicil no"
          value={sicil}
          onChange={(e) => setSicil(e.currentTarget.value)}
          data-testid="kisi-ekle-sicil"
          w={200}
        />
      </Group>
      <Text size="xs" c="dimmed">
        Kimlik numarası sonradan değiştirilemez: üç giriş kanalının eşleştirme çapasıdır. E-postası
        girilen kişi için pasif bir hesap açılır; kişi ilk girişinde onu sahiplenir.
      </Text>

      <Title order={5}>İlk istihdam (isteğe bağlı)</Title>
      <Group align="flex-end" gap="sm">
        <Select
          label="İşyeri"
          data-testid="kisi-ekle-isyeri"
          searchable
          value={workplaceId}
          onChange={setWorkplaceId}
          data={(isyerleri.data ?? []).map((w) => ({ value: w.id, label: w.ad }))}
          comboboxProps={{ withinPortal: true }}
          w={260}
        />
        <Select
          label="İşveren"
          data-testid="kisi-ekle-isveren"
          allowDeselect={false}
          searchable
          value={isveren}
          onChange={(v) => setIsveren(v ?? ANA_FIRMA)}
          data={[
            { value: ANA_FIRMA, label: 'İşyerinin şirketi' },
            ...(contractors.data ?? []).map((c) => ({
              value: c.id,
              label: `Yüklenici — ${c.ad}`,
            })),
          ]}
          w={260}
        />
        <TextInput
          label="Görev"
          value={rol}
          onChange={(e) => setRol(e.currentTarget.value)}
          data-testid="kisi-ekle-rol"
          w={220}
        />
        <TextInput
          label="Başlangıç"
          type="date"
          value={baslangic}
          onChange={(e) => setBaslangic(e.currentTarget.value)}
          data-testid="kisi-ekle-baslangic"
        />
      </Group>

      <Group>
        <Button
          data-testid="kisi-ekle-kaydet"
          disabled={!kisiDolu}
          loading={kisiAc.isPending || istihdamAc.isPending}
          onClick={kaydet}
        >
          Kişiyi kaydet
        </Button>
        <Button variant="default" component={Link} to="/calisanlar">
          Vazgeç
        </Button>
      </Group>
      <YazmaHatasi hata={kisiAc.error} testId="kisi-ekle-hata" />
      {istihdamUyarisi && (
        <Text size="sm" role="alert" data-testid="kisi-ekle-istihdam-uyarisi">
          {istihdamUyarisi}
        </Text>
      )}
    </Stack>
  );
}
